import {Trans} from '@ui/i18n/trans';
import {FileUploadProvider} from '@common/uploads/uploader/file-upload-provider';
import {useForm} from 'react-hook-form';
import {Form} from '@ui/forms/form';
import {useNavigate} from '@common/ui/navigation/use-navigate';
import React, {Fragment, useEffect, useState} from 'react';
import {Button} from '@ui/buttons/button';
import {Skeleton} from '@ui/skeleton/skeleton';
import {queryClient} from '@common/http/query-client';
import {DatatableDataQueryKey} from '@common/datatable/requests/paginated-resources';
import {RequestTitle} from '@app/titles/models/Request';
import {
  UpdateRequestPayload,
  useUpdateRequest,
} from '@app/admin/requetes/requests/use-update-request';
import {FormChipField} from '@ui/forms/input-field/chip-field/form-chip-field';
import {RequestContentSearchField} from '@app/admin/requetes/requests/request-content-search-field';
import {getTitle} from '@app/admin/titles/get-title';
import {Dialog} from '@ui/overlays/dialog/dialog';
import {DialogHeader} from '@ui/overlays/dialog/dialog-header';
import {DialogBody} from '@ui/overlays/dialog/dialog-body';
import {RequestEditorLayout} from '@app/admin/requetes/lien-editor/request-editor-layout';
import {useDialogContext} from '@ui/overlays/dialog/dialog-context';
import {useRequest} from '@app/admin/requetes/requests/use-request';
import {RequestResolvSearchField} from '@app/admin/requetes/requests/request-resolve-search-field';
import {useResolveRequest} from '@app/admin/requetes/requests/use-resolve-request';

export function RequestCompleteForm({requestId}: {requestId?: number}) {
  const request = useRequest(requestId);
  return (
    <FileUploadProvider>
      {request?.data?.request ? (
        <EditRequestForm request={request.data.request} />
      ) : (
        <Skeleton />
      )}
    </FileUploadProvider>
  );
}

interface EditRequestFormProps {
  request: RequestTitle;
}

function EditRequestForm({request}: EditRequestFormProps) {
  const form = useForm<UpdateRequestPayload>();
  const resolveRequest = useResolveRequest(request.id, form);
  const [titleId, setTitleId] = useState(0);
  const {close, formId} = useDialogContext();
  return (
    <Dialog className="min-w-[300px]" size="2xl">
      <DialogHeader padding="px-14 py-10">
        <Trans values={{id: request.id}} message="Edit request Id “:id“" />
      </DialogHeader>
      <DialogBody padding="p-0">
        <Form
          form={form}
          onSubmit={values => {
            resolveRequest.mutate(
              {...values, title_id: titleId},
              {
                onSuccess: () => {
                  queryClient.invalidateQueries({
                    queryKey: DatatableDataQueryKey('requests'),
                  });
                  close();
                },
              },
            );
          }}
        >
          <RequestEditorLayout
            actions={
              <Button
                variant="flat"
                color="primary"
                type="submit"
                disabled={resolveRequest.isPending}
              >
                <Trans message="Complete request" />
              </Button>
            }
          >
            <FormFields onSucess={id => setTitleId(id)} request={request} />
          </RequestEditorLayout>
        </Form>
      </DialogBody>
    </Dialog>
  );
}

function FormFields({
  request,
  onSucess,
}: {
  request: RequestTitle;
  onSucess?: (id: number) => void;
}) {
  const [title, setTitle] = useState(request?.post);
  const [titleId, setTitleId] = useState(0);
  return (
    <Fragment>
      <div>
        <RequestResolvSearchField
          category={request.category}
          onChange={value => {
            getTitle(value, 'title').then(data => {
              if (data.title) {
                setTitle(data.title);
                setTitleId(data.title.id);
                onSucess?.(data.title.id);
              }
            });
          }}
        />
      </div>
    </Fragment>
  );
}
